import { ColumnDefinition as NewColumnDefinition } from '@approvalmax/ui/src/components';
import { netSuiteConstants } from 'modules/common/constants';
import { backend, domain } from 'modules/data';
import { useMemo } from 'react';
import { useGetNetSuiteCustomCompanyFieldsRaw } from 'shared/data/webApp/v1';
import { getNetSuiteCustomFieldValue, sortNetSuiteCustomFields } from 'shared/helpers/(requests)';

import { CustomFieldCellComponent } from './components';
import { ColumnDefinition, CustomFieldsLine } from './useNetSuiteCustomFields.types';

const getCustomFieldCellValue = (li: CustomFieldsLine, field: domain.NetSuiteCustomField) => {
    const customField = li.customFields?.find((liField) => liField.scriptId === field.scriptId);

    if (!customField) {
        return null;
    }

    const customFieldValue = getNetSuiteCustomFieldValue(customField, true);

    if (typeof customFieldValue === 'boolean') {
        return customFieldValue === false ? null : 'true';
    }

    return customFieldValue;
};

const useSortedCustomFields = <
    Line extends {
        customFields?: domain.NetSuiteCustomField[];
    },
>(
    itemLines: Line[],
    companyCustomFields: backend.SelectNetSuiteCustomCompanyFieldsAnswer['Fields']
) => {
    const usedCustomFields: {
        [scriptId: string]: domain.NetSuiteCustomField & {
            name: string;
        };
    } = {};

    itemLines.forEach((line) => {
        line.customFields?.forEach((field) => {
            if (!usedCustomFields[field.scriptId]) {
                const fieldReference = companyCustomFields.find(
                    (companyField) => companyField.NetSuiteField.Field.ScriptId === field.scriptId
                );

                if (fieldReference) {
                    usedCustomFields[field.scriptId] = {
                        ...field,
                        name: fieldReference.NetSuiteField.Field.Name,
                    };
                }
            }
        });
    });

    return sortNetSuiteCustomFields(Object.values(usedCustomFields), companyCustomFields);
};

export const useNetSuiteLineCustomFieldsDefinition = <Line extends CustomFieldsLine>(
    itemLines: Line[],
    companyCustomFields: backend.SelectNetSuiteCustomCompanyFieldsAnswer['Fields']
) => {
    const customFieldsColumnDefinition: ColumnDefinition<Line>[] = [];

    const sortedCustomFields = useSortedCustomFields(itemLines, companyCustomFields);

    sortedCustomFields.forEach((field) => {
        if (field.fieldType && netSuiteConstants.ignoredCustomFields.includes(field.fieldType)) {
            return;
        }

        customFieldsColumnDefinition.push({
            id: `itemLineCustomField-${field.scriptId}`,
            name: field.name,
            value: (li) => getCustomFieldCellValue(li, field),
            cell: (value) => <CustomFieldCellComponent value={value} field={field} />,
            minWidth: 100,
        });
    });

    return customFieldsColumnDefinition;
};

export const useNetSuiteNewLineCustomFieldsDefinitions = <Line extends CustomFieldsLine>(
    itemLines: Line[],
    companyCustomFields: backend.SelectNetSuiteCustomCompanyFieldsAnswer['Fields']
) => {
    const customFieldsColumnDefinition: NewColumnDefinition<Line & { id: string }>[] = [];

    const sortedCustomFields = useSortedCustomFields(itemLines, companyCustomFields);

    sortedCustomFields.forEach((field) => {
        if (field.fieldType && netSuiteConstants.ignoredCustomFields.includes(field.fieldType)) {
            return;
        }

        customFieldsColumnDefinition.push({
            id: `itemLineCustomField-${field.scriptId}`,
            name: field.name,
            value: (li) => {
                const value = getCustomFieldCellValue(li, field);

                return <CustomFieldCellComponent value={value} field={field} />;
            },
            minWidth: 100,
        });
    });

    return customFieldsColumnDefinition;
};

export const useNetSuiteCompanyCustomFields = (companyId: string) => {
    const { data, isLoading } = useGetNetSuiteCustomCompanyFieldsRaw({
        query: {
            companyId,
        },
    });

    const netSuiteCompanyCustomFields = useMemo(() => data?.Fields ?? [], [data]);

    return {
        netSuiteCompanyCustomFields,
        isLoadingNetSuiteCompanyCustomFields: isLoading,
    };
};
